import type { StatusTone } from '@vnetwork/ui/data';
import type {
  NegotiationStatus,
  NegotiationPriority,
  NegotiationStage,
  NegotiationType,
} from '@/api/contract-negotiations';

/**
 * Negotiation domain formatting — status tones + human labels + filter options.
 * Verified against live API values (2026-08-27): status=draft, stage=contract_preparation,
 * priority=medium, type=quote_request observed in live data.
 *
 * Note on tone types:
 *   - negotiationStatusTone → StatusTone (used with StatusPill, which accepts 'muted')
 *   - negotiationPriorityTone → 'neutral'|'warning'|'danger'|'info' (Badge/Chip subset;
 *     neither BadgeTone nor ChipTone includes 'muted', so we use the intersection)
 */

/* ── Status ──────────────────────────────────────────────────────────────── */

export function negotiationStatusTone(status?: string): StatusTone {
  switch (status) {
    case 'completed':
    case 'converted':
      return 'success';
    case 'in_progress':
    case 'pending_review':
      return 'info';
    case 'waiting_approval':
      return 'warning';
    case 'rejected':
    case 'cancelled':
      return 'danger';
    case 'archived':
      return 'neutral';
    case 'draft':
    default:
      return 'neutral';
  }
}

export const NEGOTIATION_STATUS_LABELS: Record<NegotiationStatus, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  pending_review: 'Pending Review',
  waiting_approval: 'Waiting Approval',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  archived: 'Archived',
  converted: 'Converted',
};

export const NEGOTIATION_STATUS_OPTIONS: { value: NegotiationStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'waiting_approval', label: 'Waiting Approval' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'archived', label: 'Archived' },
  { value: 'converted', label: 'Converted' },
];

export function negotiationStatusLabel(status?: string): string {
  if (!status) return '—';
  return NEGOTIATION_STATUS_LABELS[status as NegotiationStatus] ?? status;
}

/* ── Priority ────────────────────────────────────────────────────────────── */

/** Returns a tone valid for both Badge and Chip (no 'muted' — subset of BadgeTone/ChipTone). */
export function negotiationPriorityTone(priority?: string): 'neutral' | 'warning' | 'danger' | 'info' {
  switch (priority) {
    case 'critical': return 'danger';
    case 'high': return 'warning';
    case 'medium': return 'info';
    case 'low': return 'neutral';
    default: return 'neutral';
  }
}

export const NEGOTIATION_PRIORITY_LABELS: Record<NegotiationPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const NEGOTIATION_PRIORITY_OPTIONS: { value: NegotiationPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export function negotiationPriorityLabel(priority?: string): string {
  if (!priority) return '—';
  return NEGOTIATION_PRIORITY_LABELS[priority as NegotiationPriority] ?? priority;
}

/* ── Type ────────────────────────────────────────────────────────────────── */

export const NEGOTIATION_TYPE_LABELS: Record<NegotiationType, string> = {
  quote_request: 'Quote Request',
  contract_amendment: 'Contract Amendment',
  price_negotiation: 'Price Negotiation',
  renewal: 'Renewal',
  termination: 'Termination',
};

export const NEGOTIATION_TYPE_OPTIONS: { value: NegotiationType; label: string }[] = [
  { value: 'quote_request', label: 'Quote Request' },
  { value: 'contract_amendment', label: 'Contract Amendment' },
  { value: 'price_negotiation', label: 'Price Negotiation' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'termination', label: 'Termination' },
];

export function negotiationTypeLabel(type?: string): string {
  if (!type) return '—';
  return NEGOTIATION_TYPE_LABELS[type as NegotiationType] ?? type;
}

/* ── Stage ───────────────────────────────────────────────────────────────── */

export const NEGOTIATION_STAGE_LABELS: Record<NegotiationStage, string> = {
  initial_contact: 'Initial Contact',
  needs_assessment: 'Needs Assessment',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  contract_preparation: 'Contract Preparation',
  review: 'Review',
  approval: 'Approval',
  closed: 'Closed',
};

export const NEGOTIATION_STAGE_OPTIONS: { value: NegotiationStage; label: string }[] = [
  { value: 'initial_contact', label: 'Initial Contact' },
  { value: 'needs_assessment', label: 'Needs Assessment' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'contract_preparation', label: 'Contract Preparation' },
  { value: 'review', label: 'Review' },
  { value: 'approval', label: 'Approval' },
  { value: 'closed', label: 'Closed' },
];

export function negotiationStageLabel(stage?: string): string {
  if (!stage) return '—';
  return NEGOTIATION_STAGE_LABELS[stage as NegotiationStage] ?? stage;
}
